'use client';

import { useEffect, useState, useCallback, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useRoleAuth, UserRole } from '@/contexts/RoleAuthContext';

export type NotificationEventType = 'payment_overdue' | 'memo_expired' | 'po_received';

export interface AppNotification {
  id: string;
  eventType: NotificationEventType;
  title: string;
  message: string;
  referenceId: string | null;
  referenceTable: string | null;
  isRead: boolean;
  createdAt: string;
}

// Role-based event filtering
const ROLE_ALLOWED_EVENTS: Record<UserRole, NotificationEventType[]> = {
  owner: ['payment_overdue', 'memo_expired', 'po_received'],
  manager: ['payment_overdue', 'memo_expired'],
  appraiser: ['memo_expired'],
};

function mapRow(row: any): AppNotification {
  return {
    id: row.id,
    eventType: row.event_type as NotificationEventType,
    title: row.title,
    message: row.message,
    referenceId: row.reference_id ?? null,
    referenceTable: row.reference_table ?? null,
    isRead: row.is_read,
    createdAt: row.created_at,
  };
}

export interface UseNotificationsReturn {
  notifications: AppNotification[];
  unreadCount: number;
  isLoading: boolean;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  dismiss: (id: string) => void;
}

export function useNotifications(): UseNotificationsReturn {
  const { roleUser } = useRoleAuth();
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const allowedEvents = roleUser ? ROLE_ALLOWED_EVENTS[roleUser.role] : [];
  const channelRef = useRef<ReturnType<ReturnType<typeof createClient>['channel']> | null>(null);

  const filterByRole = useCallback(
    (items: AppNotification[]) => items.filter((n) => allowedEvents.includes(n.eventType)),
    [allowedEvents]
  );

  // Initial fetch
  useEffect(() => {
    if (!roleUser) {
      setIsLoading(false);
      return;
    }

    const supabase = createClient();
    let mounted = true;

    async function fetchNotifications() {
      try {
        const { data, error } = await supabase
          .from('notifications')
          .select('*')
          .in('event_type', allowedEvents)
          .order('created_at', { ascending: false })
          .limit(50);

        if (!mounted) return;
        if (error) throw error;
        setNotifications((data || []).map(mapRow));
      } catch {
        // silently fail — notifications are non-critical
      } finally {
        if (mounted) setIsLoading(false);
      }
    }

    fetchNotifications();

    // Real-time subscription
    const supabaseClient = createClient();
    const channel = supabaseClient
      .channel('notifications_realtime')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'notifications' },
        (payload) => {
          if (!mounted) return;
          const newNotif = mapRow(payload.new);
          if (!allowedEvents.includes(newNotif.eventType)) return;
          setNotifications((prev) => [newNotif, ...prev].slice(0, 50));
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'notifications' },
        (payload) => {
          if (!mounted) return;
          const updated = mapRow(payload.new);
          if (!allowedEvents.includes(updated.eventType)) return;
          setNotifications((prev) => prev.map((n) => (n.id === updated.id ? updated : n)));
        }
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'notifications' },
        (payload) => {
          if (!mounted) return;
          setNotifications((prev) => prev.filter((n) => n.id !== payload.old?.id));
        }
      )
      // Also listen to sales for payment_overdue events
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'sales' },
        async (payload) => {
          if (!mounted) return;
          if (!allowedEvents.includes('payment_overdue')) return;
          const row = payload.new;
          if (row.payment_status === 'OVERDUE') {
            const supabaseInner = createClient();
            await supabaseInner.from('notifications').insert({
              event_type: 'payment_overdue',
              title: 'Payment Overdue',
              message: `Invoice ${row.invoice_number} from ${row.customer_name} is overdue. Amount: $${Number(row.sale_price_usd).toLocaleString('en-US', { minimumFractionDigits: 0 })}.`,
              reference_id: row.id,
              reference_table: 'sales',
              is_read: false,
            });
          }
        }
      )
      // Listen to memos for memo_expired events
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'memos' },
        async (payload) => {
          if (!mounted) return;
          if (!allowedEvents.includes('memo_expired')) return;
          const row = payload.new;
          if (row.memo_status === 'OVERDUE') {
            const supabaseInner = createClient();
            await supabaseInner.from('notifications').insert({
              event_type: 'memo_expired',
              title: 'Memo Expired',
              message: `Memo ${row.memo_number} for ${row.customer_name} has passed its return due date. SKU: ${row.sku || 'N/A'}.`,
              reference_id: row.id,
              reference_table: 'memos',
              is_read: false,
            });
          }
        }
      )
      // Listen to purchases for po_received events
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'purchases' },
        async (payload) => {
          if (!mounted) return;
          if (!allowedEvents.includes('po_received')) return;
          const row = payload.new;
          const oldRow = payload.old;
          if (row.po_status === 'RECEIVED' && oldRow?.po_status !== 'RECEIVED') {
            const supabaseInner = createClient();
            await supabaseInner.from('notifications').insert({
              event_type: 'po_received',
              title: 'PO Received',
              message: `Purchase Order ${row.po_number} from ${row.supplier_name} has been received. SKU: ${row.sku || 'N/A'}.`,
              reference_id: row.id,
              reference_table: 'purchases',
              is_read: false,
            });
          }
        }
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      mounted = false;
      supabaseClient.removeChannel(channel);
    };
  }, [roleUser, allowedEvents]);

  const markAsRead = useCallback(async (id: string) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)));
    try {
      const supabase = createClient();
      await supabase.from('notifications').update({ is_read: true }).eq('id', id);
    } catch {
      // revert on failure
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: false } : n)));
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    try {
      const supabase = createClient();
      await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('is_read', false)
        .in('event_type', allowedEvents);
    } catch {
      // silently fail
    }
  }, [allowedEvents]);

  const dismiss = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const unreadCount = filterByRole(notifications).filter((n) => !n.isRead).length;

  return {
    notifications: filterByRole(notifications),
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    dismiss,
  };
}
